import { useMemo, useState } from 'react'
import { Button, Group, Stack, Text } from '@mantine/core'
import type { Patient } from '@medplum/fhirtypes'
import { RecordsFacilityPicker } from './RecordsFacilityPicker'
import { AddRecordsFacilityForm } from './AddRecordsFacilityForm'
import { useRecordsFacilities } from './useRecordsFacilities'
import type { SavedFacility } from './useRecordsFacilities'
import { useRecordLocationSuggestions } from './useRecordLocationSuggestions'
import { formatFacilityAddress, normalizeFacilityName } from './authorizationFormData'
import type { AuthorizationFormState, FacilityDetails } from './authorizationFormData'

type Props = {
  patient: Patient
  values: AuthorizationFormState
  setFacility: (facility: FacilityDetails) => void
  onCancel: () => void
  onNext: () => void
}

export const FacilityStep = ({ patient, values, setFacility, onCancel, onNext }: Props) => {
  const [mode, setMode] = useState<'pick' | 'add'>('pick')
  const [searchQuery, setSearchQuery] = useState('')
  const { facilities, loading, refetch } = useRecordsFacilities(searchQuery)
  const [chosen, setChosen] = useState<FacilityDetails | null>(
    values.facility.name ? values.facility : null,
  )

  // Names of places the patient has been (their record locations), normalized
  // for the green "been here" indicator on matching facilities.
  const { suggestions: recordLocations } = useRecordLocationSuggestions(patient)
  const visitedFacilityNames = useMemo(
    () => new Set(recordLocations.map((location) => normalizeFacilityName(location.name))),
    [recordLocations],
  )

  const toDetails = (f: SavedFacility): FacilityDetails => ({
    name: f.name,
    street: f.street,
    city: f.city,
    state: f.state,
    zip: f.zip,
    fax: f.fax,
    phone: f.phone,
    organizationId: f.id,
  })

  if (mode === 'add') {
    return (
      <AddRecordsFacilityForm
        patient={patient}
        onSaved={(facility) => {
          setChosen(facility)
          refetch()
          setMode('pick')
        }}
        onCancel={() => setMode('pick')}
      />
    )
  }

  return (
    <Stack gap="md">
      <RecordsFacilityPicker
        facilities={facilities}
        loading={loading}
        visitedFacilityNames={visitedFacilityNames}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedFacilityId={chosen?.organizationId}
        onSelect={(f) => setChosen(toDetails(f))}
        onAddNew={() => setMode('add')}
        onFaxSaved={(facilityId, faxNumber) => {
          refetch()
          setChosen((prev) =>
            prev && prev.organizationId === facilityId ? { ...prev, fax: faxNumber } : prev,
          )
        }}
      />

      {chosen && (
        <Text size="sm">
          Selected: <b>{chosen.name}</b> — {formatFacilityAddress(chosen)}
        </Text>
      )}

      <Group justify="space-between">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="teal"
          disabled={!chosen}
          onClick={() => {
            if (chosen) {
              setFacility(chosen)
              onNext()
            }
          }}
        >
          Next: Details
        </Button>
      </Group>
    </Stack>
  )
}
